import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import {
  CalendarDays, Layers, Building2, User, Clock, Wallet, Flag, Receipt,
  ChevronLeft, ChevronRight, X, Check, Loader2, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VAT_RATE = 20;
const SHORT_DAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const tl = (n) => `₺${(Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const trDate = (d) => (d ? String(d).slice(0, 10).split('-').reverse().join('.') : '-');
const todayISO = () => new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);
const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

const durationHours = (start, end) => {
  if (!start || !end) return 1;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins <= 0) mins += 24 * 60;
  return Math.round((mins / 60) * 100) / 100;
};

const vatPreview = (amount) => {
  const base = parseFloat(amount) || 0;
  const vat = Math.round(base * VAT_RATE) / 100;
  return { base, vat, total: Math.round((base + vat) * 100) / 100 };
};

const EMPTY = {
  salon_id: '', customer_id: '', date: todayISO(), start_time: '', end_time: '',
  payment_method: 'nakit', paid_amount: '', total_amount: '',
  referee_id: 'null', referee_fee: '', invoice_issued: false,
  invoice_due_date: '', notes: '',
};

const Section = ({ icon: Icon, title, hint, children, testid }) => (
  <section className="rounded-xl border border-[#E6EDF3] bg-white" data-testid={testid}>
    <header className="flex items-center gap-2 px-4 py-2.5 border-b border-[#F1F5F9]">
      <Icon className="w-4 h-4 text-[#176B91]" />
      <h4 className="text-[13px] font-semibold text-slate-800 tracking-wide">{title}</h4>
      {hint && <span className="text-[11px] text-slate-400 ml-auto">{hint}</span>}
    </header>
    <div className="p-4 space-y-3">{children}</div>
  </section>
);

/** Çoklu tarih seçimi için aylık takvim */
const MultiDateCalendar = ({ selected, onToggle }) => {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const first = new Date(ym.y, ym.m, 1);
  const lead = (first.getDay() + 6) % 7;
  const days = new Date(ym.y, ym.m + 1, 0).getDate();
  const shift = (delta) => {
    const d = new Date(ym.y, ym.m + delta, 1);
    setYm({ y: d.getFullYear(), m: d.getMonth() });
  };
  return (
    <div data-testid="bulk-calendar">
      <div className="flex items-center justify-between mb-2">
        <Button type="button" variant="outline" size="sm" className="h-7 w-7 p-0"
          onClick={() => shift(-1)} data-testid="bulk-cal-prev"><ChevronLeft className="w-3.5 h-3.5" /></Button>
        <span className="text-[13px] font-semibold text-slate-700">{MONTHS[ym.m]} {ym.y}</span>
        <Button type="button" variant="outline" size="sm" className="h-7 w-7 p-0"
          onClick={() => shift(1)} data-testid="bulk-cal-next"><ChevronRight className="w-3.5 h-3.5" /></Button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {SHORT_DAYS.map((d) => <div key={d} className="text-center text-[10px] font-semibold text-slate-400">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: lead }).map((_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: days }).map((_, i) => {
          const ds = iso(ym.y, ym.m, i + 1);
          const on = selected.includes(ds);
          return (
            <button key={ds} type="button" onClick={() => onToggle(ds)} data-testid={`bulk-cal-day-${ds}`}
              className={`h-8 rounded-md text-[12px] font-medium transition-colors ${on
                ? 'bg-[#176B91] text-white shadow-sm'
                : 'bg-slate-50 text-slate-600 hover:bg-[#E6F2F7]'}`}>
              {i + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const NewReservationDialog = ({ open, onOpenChange, salons = [], customers = [], referees = [], prefill, onCreated }) => {
  const [mode, setMode] = useState('single');
  const [form, setForm] = useState(EMPTY);
  const [custQuery, setCustQuery] = useState('');
  const [saving, setSaving] = useState(false);
  // Toplu seçim
  const [dates, setDates] = useState([]);
  const [gen, setGen] = useState({ from: todayISO(), to: '', weekdays: [] });
  const [dueMode, setDueMode] = useState('days'); // 'days' | 'common'
  const [dueDays, setDueDays] = useState('15');
  const [result, setResult] = useState(null);
  // Boş saat önerisi
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const slotDate = mode === 'single' ? form.date : (dates[0] || '');

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const hasReferee = form.referee_id && form.referee_id !== 'null';

  // Takvimden hızlı rezervasyon ile gelen ön doldurma
  React.useEffect(() => {    if (!open || !prefill) return;
    if (!prefill.salon_id && !prefill.customer_id && !prefill.start_time) return;
    setForm((f) => ({
      ...f,
      salon_id: prefill.salon_id || f.salon_id,
      customer_id: prefill.customer_id || f.customer_id,
      date: prefill.date || f.date,
      start_time: prefill.start_time || f.start_time,
    }));
  }, [open, prefill]);

  const filteredCustomers = useMemo(() => {
    const q = custQuery.trim().toLowerCase();
    if (!q) return customers.slice(0, 200);
    return customers.filter((c) =>
      c.name?.toLowerCase().includes(q) || c.phone?.includes(q) || c.email?.toLowerCase().includes(q)
    ).slice(0, 200);
  }, [customers, custQuery]);

  // Seçili salon + gün için boş/dolu saat önerisi
  React.useEffect(() => {
    if (!open || !form.salon_id || !slotDate) { setSlots([]); return; }
    let cancelled = false;
    setSlotsLoading(true);
    axios.get(`${API}/occupancy/report`, {
      params: { date_from: slotDate, date_to: slotDate, salon_id: form.salon_id },
    }).then((res) => {
      if (cancelled) return;
      const day = res.data.salons?.[0]?.days?.[0];
      setSlots(day && !day.closed ? day.slots : []);
    }).catch(() => { if (!cancelled) setSlots([]); })
      .finally(() => { if (!cancelled) setSlotsLoading(false); });
    return () => { cancelled = true; };
  }, [open, form.salon_id, slotDate]);

  const reset = () => {
    setForm(EMPTY); setDates([]); setCustQuery(''); setResult(null);
    setGen({ from: todayISO(), to: '', weekdays: [] });
  };

  const toggleDate = (ds) => setDates((p) => (p.includes(ds) ? p.filter((x) => x !== ds) : [...p, ds].sort()));
  const toggleWeekday = (i) => setGen((g) => ({
    ...g, weekdays: g.weekdays.includes(i) ? g.weekdays.filter((x) => x !== i) : [...g.weekdays, i],
  }));

  const generateDates = () => {
    if (!gen.from || !gen.to) { toast.error('Tarih aralığı seçin'); return; }
    if (!gen.weekdays.length) { toast.error('En az bir gün seçin'); return; }
    const start = new Date(`${gen.from}T00:00:00`);
    const end = new Date(`${gen.to}T00:00:00`);
    if (end < start) { toast.error('Bitiş tarihi başlangıçtan önce olamaz'); return; }
    const out = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const wd = (d.getDay() + 6) % 7;
      if (gen.weekdays.includes(wd)) out.push(iso(d.getFullYear(), d.getMonth(), d.getDate()));
      if (out.length > 400) break;
    }
    if (!out.length) { toast.error('Bu aralıkta seçilen günlere ait tarih bulunamadı'); return; }
    setDates((p) => Array.from(new Set([...p, ...out])).sort());
    toast.success(`${out.length} tarih üretildi`);
  };

  const quickRange = (months) => {
    const s = new Date(`${gen.from || todayISO()}T00:00:00`);
    const e = new Date(s); e.setMonth(e.getMonth() + months);
    setGen((g) => ({ ...g, from: g.from || todayISO(), to: iso(e.getFullYear(), e.getMonth(), e.getDate()) }));
  };

  const validateShared = () => {
    if (!form.salon_id) return 'Salon seçin';
    if (!form.customer_id) return 'Müşteri seçin';
    if (!form.start_time || !form.end_time) return 'Başlangıç ve bitiş saati girin';
    if (form.invoice_issued && mode === 'single' && !form.invoice_due_date) return 'Faturalı işlemde vade tarihi zorunludur';
    if (form.invoice_issued && mode === 'bulk' && dueMode === 'common' && !form.invoice_due_date) return 'Ortak vade tarihi seçin';
    if (form.invoice_issued && mode === 'bulk' && dueMode === 'days' && !String(dueDays).trim()) return 'Vade gün sayısı girin';
    return null;
  };

  const submitSingle = async (e) => {
    e.preventDefault();
    const err = validateShared();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/reservations`, {
        salon_id: form.salon_id, customer_id: form.customer_id, date: form.date,
        start_time: form.start_time, end_time: form.end_time,
        duration_hours: durationHours(form.start_time, form.end_time),
        payment_method: form.payment_method,
        paid_amount: parseFloat(form.paid_amount) || 0,
        total_amount: form.total_amount !== '' ? parseFloat(form.total_amount) : null,
        referee_id: form.referee_id === 'null' ? null : form.referee_id,
        referee_fee: form.referee_fee ? parseFloat(form.referee_fee) : null,
        invoice_issued: form.invoice_issued,
        invoice_due_date: form.invoice_issued ? form.invoice_due_date : null,
        notes: form.notes || null,
      });
      toast.success('Rezervasyon başarıyla oluşturuldu');
      reset();
      onOpenChange(false);
      onCreated?.();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || 'Rezervasyon oluşturulamadı');
    } finally { setSaving(false); }
  };

  const submitBulk = async (e) => {
    e.preventDefault();
    const err = validateShared();
    if (err) { toast.error(err); return; }
    if (!dates.length) { toast.error('En az bir tarih seçin'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/reservations/bulk`, {
        salon_id: form.salon_id, customer_id: form.customer_id, dates,
        start_time: form.start_time, end_time: form.end_time,
        duration_hours: durationHours(form.start_time, form.end_time),
        payment_method: form.payment_method,
        total_amount: form.total_amount !== '' ? parseFloat(form.total_amount) : null,
        referee_id: form.referee_id === 'null' ? null : form.referee_id,
        referee_fee: form.referee_fee ? parseFloat(form.referee_fee) : null,
        invoice_issued: form.invoice_issued,
        invoice_due_date: form.invoice_issued && dueMode === 'common' ? form.invoice_due_date : null,
        invoice_due_days: form.invoice_issued && dueMode === 'days' ? parseInt(dueDays, 10) : null,
        notes: form.notes || null,
      });
      setResult(res.data);
      toast.success(`${res.data.created_count} rezervasyon oluşturuldu`);
      setDates([]);
      onCreated?.();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || 'Toplu rezervasyon oluşturulamadı');
    } finally { setSaving(false); }
  };

  const SharedFields = (
    <>
      <Section icon={Building2} title="Salon & Müşteri">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] text-slate-500">Salon</Label>
            <Select value={form.salon_id} onValueChange={(v) => set({ salon_id: v })}>
              <SelectTrigger className="h-9" data-testid="res-salon-select"><SelectValue placeholder="Salon seçin" /></SelectTrigger>
              <SelectContent>
                {salons.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Müşteri</Label>
            <div className="flex gap-2">
              <Input className="h-9 w-32" placeholder="Ara..." value={custQuery}
                onChange={(e) => setCustQuery(e.target.value)} data-testid="res-customer-search" />
              <Select value={form.customer_id} onValueChange={(v) => set({ customer_id: v })}>
                <SelectTrigger className="h-9 flex-1" data-testid="res-customer-select"><SelectValue placeholder="Müşteri seçin" /></SelectTrigger>
                <SelectContent>
                  {filteredCustomers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}{c.phone ? ` (${c.phone})` : ''}</SelectItem>
                  ))}
                  {filteredCustomers.length === 0 && (
                    <div className="p-2 text-sm text-slate-500 text-center">Müşteri bulunamadı</div>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </Section>

      <Section icon={Wallet} title="Ücret & Ödeme"
        hint={form.invoice_issued ? "Faturalıda KDV'siz matrah girin" : undefined}>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] text-slate-500">Maç Tutarı (₺) — boş ise salon tarifesinden hesaplanır</Label>
            <Input type="number" step="0.01" className="h-9" value={form.total_amount}
              onChange={(e) => set({ total_amount: e.target.value })} placeholder="Örn: 1500.00"
              data-testid="reservation-total-amount-input" />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Ödeme Yöntemi (Hangi Kasaya?)</Label>
            <Select value={form.payment_method} onValueChange={(v) => set({ payment_method: v })}>
              <SelectTrigger className="h-9" data-testid="reservation-payment-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="nakit">Nakit</SelectItem>
                <SelectItem value="garanti_bbva">Garanti BBVA</SelectItem>
                <SelectItem value="bostanci">Bostancı</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mode === 'single' && (
            <div>
              <Label className="text-[11px] text-slate-500">Ödenen / Kapora Tutarı (₺)</Label>
              <Input type="number" step="0.01" className="h-9" value={form.paid_amount}
                onChange={(e) => set({ paid_amount: e.target.value })} placeholder="0.00 (boş = ödeme yok)"
                data-testid="reservation-paid-amount-input" />
            </div>
          )}
        </div>
      </Section>

      <Section icon={Flag} title="Hakem" hint="Opsiyonel">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] text-slate-500">Hakem</Label>
            <Select value={form.referee_id} onValueChange={(v) => set({ referee_id: v })}>
              <SelectTrigger className="h-9" data-testid="res-referee-select"><SelectValue placeholder="Hakem seçin" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="null">Hakem Yok</SelectItem>
                {referees.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {hasReferee && (
            <div>
              <Label className="text-[11px] text-slate-500">Hakem Ücreti (₺)</Label>
              <Input type="number" step="0.01" className="h-9" value={form.referee_fee}
                onChange={(e) => set({ referee_fee: e.target.value })} placeholder="0.00"
                data-testid="res-referee-fee-input" />
            </div>
          )}
        </div>
      </Section>

      <Section icon={Receipt} title="Fatura & Vade">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" className="h-4 w-4 rounded border-gray-300" checked={form.invoice_issued}
            onChange={(e) => set({ invoice_issued: e.target.checked, invoice_due_date: e.target.checked ? '' : '' })}
            data-testid="reservation-invoice-checkbox" />
          <span className="text-[13px] font-semibold text-amber-900">Faturalı İşlem (+%{VAT_RATE} KDV otomatik eklenir)</span>
        </label>

        {form.invoice_issued && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-3" data-testid="res-invoice-block">
            <div className="grid grid-cols-3 gap-2 text-xs text-amber-900" data-testid="reservation-vat-preview">
              <div>Matrah<div className="font-bold text-sm">{tl(vatPreview(form.total_amount).base)}</div></div>
              <div>KDV %{VAT_RATE}<div className="font-bold text-sm">{tl(vatPreview(form.total_amount).vat)}</div></div>
              <div>Toplam<div className="font-bold text-sm text-emerald-700">{tl(vatPreview(form.total_amount).total)}</div></div>
            </div>

            {mode === 'bulk' && (
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={dueMode === 'days' ? 'default' : 'outline'}
                  className="h-8 text-[12px]" onClick={() => setDueMode('days')} data-testid="res-due-mode-days">
                  Tarihten X gün sonra
                </Button>
                <Button type="button" size="sm" variant={dueMode === 'common' ? 'default' : 'outline'}
                  className="h-8 text-[12px]" onClick={() => setDueMode('common')} data-testid="res-due-mode-common">
                  Tek ortak vade
                </Button>
              </div>
            )}

            {mode === 'bulk' && dueMode === 'days' ? (
              <div>
                <Label className="text-[11px] text-amber-900">Vade Gün Sayısı (her rezervasyon tarihine eklenir)</Label>
                <Input type="number" min="0" className="h-9 bg-white" value={dueDays}
                  onChange={(e) => setDueDays(e.target.value)} data-testid="res-due-days-input" />
              </div>
            ) : (
              <div>
                <Label className="text-[11px] text-amber-900">Vade Tarihi (tahsilat son günü)</Label>
                <Input type="date" className="h-9 bg-white" value={form.invoice_due_date}
                  onChange={(e) => set({ invoice_due_date: e.target.value })}
                  data-testid="reservation-invoice-due-date-input" />
              </div>
            )}
          </div>
        )}
      </Section>

      <Section icon={Sparkles} title="Notlar" hint="Opsiyonel">
        <Textarea value={form.notes} onChange={(e) => set({ notes: e.target.value })}
          placeholder="Ek notlar..." data-testid="res-notes-input" />
      </Section>
    </>
  );

  const TimeFields = (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-3 gap-3">
        {mode === 'single' && (
          <div>
            <Label className="text-[11px] text-slate-500">Tarih</Label>
            <Input type="date" className="h-9" value={form.date}
              onChange={(e) => set({ date: e.target.value })} data-testid="res-date-input" />
          </div>
        )}
        <div>
          <Label className="text-[11px] text-slate-500">Başlangıç</Label>
          <Input type="time" className="h-9" value={form.start_time}
            onChange={(e) => set({ start_time: e.target.value })} data-testid="res-start-time-input" />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Bitiş</Label>
          <Input type="time" className="h-9" value={form.end_time}
            onChange={(e) => set({ end_time: e.target.value })} data-testid="res-end-time-input" />
        </div>
      </div>

      {form.salon_id && slotDate && (
        <div className="rounded-lg border border-[#E6EDF3] bg-[#F8FAFC] p-3" data-testid="free-slots-block">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[12px] font-semibold text-slate-700">
              Boş Saatler · {trDate(slotDate)}
              {mode === 'bulk' && <span className="text-slate-400 font-normal"> (ilk seçili tarihe göre)</span>}
            </p>
            {slotsLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
          </div>
          {slots.length === 0 && !slotsLoading ? (
            <p className="text-[11px] text-slate-500">Bu gün salon kapalı veya çalışma saati tanımlı değil</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {slots.map((sl) => {
                const active = form.start_time === sl.hour;
                return (
                  <button key={sl.hour} type="button" disabled={sl.busy}
                    onClick={() => set({ start_time: sl.hour, end_time: sl.end })}
                    title={sl.busy ? `Dolu · ${sl.customer_name || ''}` : 'Boş — seçmek için tıklayın'}
                    data-testid={`free-slot-${sl.hour}`}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors ${sl.busy
                      ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed line-through'
                      : active
                        ? 'bg-[#176B91] text-white border-[#176B91]'
                        : 'bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50'}`}>
                    {sl.hour}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Yeni Rezervasyon</DialogTitle>
          <DialogDescription>Tek rezervasyon veya dönemsel (6 aylık) toplu rezervasyon oluşturun</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 p-1 rounded-lg bg-slate-100 w-fit">
          <button type="button" onClick={() => setMode('single')} data-testid="res-tab-single"
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[13px] font-semibold transition-colors ${mode === 'single' ? 'bg-white text-[#176B91] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <CalendarDays className="w-3.5 h-3.5" /> Tek Rezervasyon
          </button>
          <button type="button" onClick={() => setMode('bulk')} data-testid="res-tab-bulk"
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[13px] font-semibold transition-colors ${mode === 'bulk' ? 'bg-white text-[#176B91] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <Layers className="w-3.5 h-3.5" /> Toplu Rezervasyon
          </button>
        </div>

        <form onSubmit={mode === 'single' ? submitSingle : submitBulk} className="space-y-3 mt-2">
          <Section icon={Clock} title={mode === 'single' ? 'Tarih & Saat' : 'Saat Aralığı'}
            hint={mode === 'bulk' ? 'Tüm seçili tarihlere uygulanır' : undefined}>
            {TimeFields}
          </Section>

          {mode === 'bulk' && (
            <Section icon={CalendarDays} title="Tarih Seçimi" hint={`${dates.length} tarih seçildi`} testid="bulk-date-section">
              <div className="rounded-lg border border-[#E6EDF3] p-3 space-y-3">
                <p className="text-[12px] font-semibold text-slate-700">1) Haftanın günleri + tarih aralığı</p>
                <div className="flex flex-wrap gap-1">
                  {SHORT_DAYS.map((d, i) => (
                    <button key={d} type="button" onClick={() => toggleWeekday(i)} data-testid={`bulk-weekday-${i}`}
                      className={`px-3 h-8 rounded-md text-[12px] font-semibold transition-colors ${gen.weekdays.includes(i)
                        ? 'bg-[#176B91] text-white' : 'bg-slate-50 text-slate-600 hover:bg-[#E6F2F7]'}`}>{d}</button>
                  ))}
                </div>
                <div className="grid sm:grid-cols-3 gap-2">
                  <div>
                    <Label className="text-[11px] text-slate-500">Başlangıç</Label>
                    <Input type="date" className="h-9" value={gen.from}
                      onChange={(e) => setGen({ ...gen, from: e.target.value })} data-testid="bulk-range-from" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500">Bitiş</Label>
                    <Input type="date" className="h-9" value={gen.to}
                      onChange={(e) => setGen({ ...gen, to: e.target.value })} data-testid="bulk-range-to" />
                  </div>
                  <div className="flex items-end gap-1">
                    <Button type="button" variant="outline" size="sm" className="h-9 text-[11px]"
                      onClick={() => quickRange(3)} data-testid="bulk-quick-3m">3 Ay</Button>
                    <Button type="button" variant="outline" size="sm" className="h-9 text-[11px]"
                      onClick={() => quickRange(6)} data-testid="bulk-quick-6m">6 Ay</Button>
                    <Button type="button" size="sm" className="h-9 text-[12px] bg-[#176B91] hover:bg-[#12566F]"
                      onClick={generateDates} data-testid="bulk-generate-btn">Tarihleri Üret</Button>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-[#E6EDF3] p-3">
                <p className="text-[12px] font-semibold text-slate-700 mb-2">2) Takvimden tek tek seç / çıkar</p>
                <MultiDateCalendar selected={dates} onToggle={toggleDate} />
              </div>

              {dates.length > 0 && (
                <div className="rounded-lg border border-[#E6EDF3] p-3" data-testid="bulk-selected-list">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[12px] font-semibold text-slate-700">
                      Seçilen tarihler (<span data-testid="bulk-selected-count">{dates.length}</span>)
                    </p>
                    <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]"
                      onClick={() => setDates([])} data-testid="bulk-clear-dates">Temizle</Button>
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                    {dates.map((d) => (
                      <span key={d} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#E6F2F7] text-[11px] text-[#12566F]">
                        {trDate(d)}
                        <button type="button" onClick={() => toggleDate(d)} data-testid={`bulk-remove-${d}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Section>
          )}

          {SharedFields}

          {result && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-[12px]" data-testid="bulk-result">
              <p className="font-semibold text-emerald-800 flex items-center gap-1.5">
                <Check className="w-4 h-4" /> {result.created_count} rezervasyon oluşturuldu
                {result.skipped_count > 0 && <span className="text-amber-700"> · {result.skipped_count} tarih atlandı</span>}
              </p>
              {result.skipped_count > 0 && (
                <ul className="mt-1 space-y-0.5 text-amber-800 max-h-24 overflow-y-auto">
                  {result.skipped.map((s) => <li key={s.date}>{trDate(s.date)} — {s.reason}</li>)}
                </ul>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => { reset(); onOpenChange(false); }}>Kapat</Button>
            <Button type="submit" disabled={saving} className="bg-[#176B91] hover:bg-[#12566F]"
              data-testid={mode === 'single' ? 'res-submit-single' : 'res-submit-bulk'}>
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              {mode === 'single' ? 'Rezervasyon Oluştur' : `${dates.length} Rezervasyon Oluştur`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewReservationDialog;
